import { APIService } from "shared/services";
import { API_ENDPOINTS, API_ERROR_CODES } from "shared/constants";
import { IDataKeys, IJSONAPIItem, IAPIError } from "shared/types";
import { IBandInvitationProps } from "Bands/types";
import { merge } from "lodash";

interface IJSONAPIBandInvitation {
  [key: string]: IJSONAPIItem;
}

export interface IJSONAPIBandInvitationCollection {
  [IDataKeys.bandInvitation]: IJSONAPIBandInvitation;
  meta: {
    [key: string]: IJSONAPIItem;
  };
  pagination: any;
}

export class BandInvitationService {
  static getBandInvitations = (
    queryParams?: string | { [key: string]: string | number | undefined | string[] }
  ): Promise<IJSONAPIBandInvitationCollection> =>
    new Promise(async (resolve, reject) => {
      try {
        const invitations: unknown = await APIService.get({
          path: API_ENDPOINTS.BAND_INVITATIONS,
          data: queryParams,
        });

        resolve(
          merge(
            { [IDataKeys.bandInvitation]: {} }, // if data is empty, return empty object for key
            invitations
          ) as IJSONAPIBandInvitationCollection
        );
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.GET_BAND_INVITATIONS);
        return reject(error);
      }
    });

  static getBandInvitation = (id: string): Promise<IJSONAPIBandInvitation> =>
    new Promise(async (resolve, reject) => {
      try {
        const response = await APIService.get({
          path: API_ENDPOINTS.BAND_INVITATION(id),
        });
        resolve(response);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.GET_BAND_INVITATION);
        return reject(error);
      }
    });

  static createBandInvitation = (data: IBandInvitationProps): Promise<IJSONAPIBandInvitation> =>
    new Promise(async (resolve, reject) => {
      try {
        const response = await APIService.post({
          path: API_ENDPOINTS.BAND_INVITATIONS,
          data,
        });
        resolve(response);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.POST_BAND_INVITATION);
        return reject(error);
      }
    });

  static editBandInvitation = ({
    id,
    data,
  }: {
    id: string;
    data: Partial<IBandInvitationProps>;
  }): Promise<IJSONAPIBandInvitation> =>
    new Promise(async (resolve, reject) => {
      try {
        const response = await APIService.patch({
          path: API_ENDPOINTS.BAND_INVITATION(id),
          data,
        });
        resolve(response);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.PATCH_BAND_INVITATION);
        return reject(error);
      }
    });

  static deleteBandInvitation = (id: string): Promise<void> =>
    new Promise(async (resolve, reject) => {
      try {
        await APIService.delete({
          path: API_ENDPOINTS.BAND_INVITATION(id),
        });
        resolve();
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.DELETE_BAND_INVITATION);
        return reject(error);
      }
    });

  static sendBandInvitation = (id: string): Promise<void> =>
    new Promise(async (resolve, reject) => {
      try {
        await APIService.post({
          path: API_ENDPOINTS.SEND_INVITATION(id),
          isJSON: true,
        });
        resolve();
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.POST_SEND_BAND_INVITATION);
        return reject(error);
      }
    });

  static acceptBandInvitation = (id: string): Promise<void> =>
    new Promise(async (resolve, reject) => {
      try {
        await APIService.post({
          path: API_ENDPOINTS.ACCEPT_INVITATION(id),
          isJSON: true,
        });
        resolve();
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.POST_ACCEPT_BAND_INVITATION);
        return reject(error);
      }
    });

  static revokeBandInvitation = (id: string): Promise<void> =>
    new Promise(async (resolve, reject) => {
      try {
        await APIService.post({
          path: API_ENDPOINTS.REVOKE_INVITATION(id),
          isJSON: true,
        });
        resolve();
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.POST_REVOKE_BAND_INVITATION);
        return reject(error);
      }
    });
}
